import { useState, useEffect } from "react";
import { Compass } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface EffectiveDomain {
  id: string;
  domain_key: string;
  name_en: string;
  name_zh: string;
  description_en?: string;
  description_zh?: string;
  icon_name?: string;
  sort_order: number;
}

interface DomainFilterProps {
  selectedDomain: string;
  onDomainChange: (domainKey: string) => void;
}

const DomainFilter = ({ selectedDomain, onDomainChange }: DomainFilterProps) => {
  const { t, i18n } = useTranslation('practitioners');
  const [domains, setDomains] = useState<EffectiveDomain[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Fetch effective domains from Supabase
  useEffect(() => {
    const fetchDomains = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('effective_domains')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (error) throw error;
        
        setDomains(data || []);
      } catch (error) {
        console.error("Error fetching effective domains:", error);
        toast({
          variant: 'destructive',
          title: "Failed to load domains",
          description: "Please try refreshing the page.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDomains();
  }, [toast]);

  // Get display name based on current language
  const getDisplayName = (domain: EffectiveDomain) => {
    if (i18n.language === 'zh') {
      return domain.name_zh;
    }
    return domain.name_en;
  };

  return (
    <div>
      <h3 className="text-sm font-medium mb-2 flex items-center">
        <Compass className="mr-2 h-4 w-4 text-amber-600" /> {t('effectiveDomain')}
      </h3>
      
      <Select 
        value={selectedDomain} 
        onValueChange={onDomainChange}
        disabled={isLoading}
      >
        <SelectTrigger className="border-amber-200">
          <SelectValue placeholder={t('allDomains')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all_domains">{t('allDomains')}</SelectItem>
          {domains.map((domain) => (
            <SelectItem key={domain.id} value={domain.domain_key}>
              {getDisplayName(domain)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default DomainFilter;
